/**
 * validation.js
 * 
 * Validation schemas and format components for defect forms.
 * Supports both old and new form structures.
 * 
 * Exports:
 * - validationSchema: Yup validation schema for defect forms
 * - NumericFormatCustom: Format component for currency inputs
 * - NumberFormatForPercentage: Format component for percentage inputs
 */

import PropTypes from "prop-types";
import { NumericFormat } from 'react-number-format';
import * as yup from "yup";

// ===== VALIDATION SCHEMA =====

export const validationSchema = yup.object().shape({
  // Element validation - support both old and new forms
  // Accept string during typing (user input), transform to object on submit
  // This flexible approach allows seamless UX without blocking validation
  elementName: yup.mixed().when(['isElementOther'], {
    is: (isElementOther) => isElementOther,
    then: () => yup.mixed().test(
      'elementName-validation',
      'Element name is required',
      function(value) {
        // Accept non-empty strings (during typing - good UX!)
        if (typeof value === 'string' && value.trim().length > 0) {
          return true; // ✅ Valid string input
        }
        // Accept valid objects (after transformation or from old form)
        if (value && typeof value === 'object' && value.value) {
          return true; // ✅ Valid object format
        }
        // Reject null, undefined, empty string, or invalid objects
        return false; // ❌ Invalid
      }
    ),
    otherwise: () => yup.mixed().nullable()
  }),
  elementType: yup.string().when(['isElementOther'], {
    is: (isElementOther) => !isElementOther,
    then: () => yup.string().required("Element type is required"),
    otherwise: () => yup.string().nullable()
  }),
  riskType: yup.string().required("This is a Required field"),
  defectType: yup.string().required("This is a Required field"),
  priority: yup.string().required("Priority is required"),
  priorityType: yup.string().nullable(),
  defectDescription: yup.string().required("This is a Required field"),
  measureDescription: yup.string().required("This is a Required field"),
  pricePerUnit: yup.number().when(["costDefect"], {
    is: true,
    then:() => yup
      .number()
      .required("This is a Required field")
      .min(1)
      .typeError("This should be a valid Cost"),
    otherwise:()=> yup.number().nullable(),
  }),
  quantity:yup.number().when(["costDefect"], {
    is: true,
    then:() => yup
      .number()
      .required("This is a Required field")
      .min(1)
      .typeError("This should be a valid Quantity"),
    otherwise:()=> yup.number().nullable(),
  }),
  extraCostComment: yup.string().when(["extraCost", "costDefect"], {
    is: (extraCost, costDefect) =>
      extraCost != null && Number(extraCost) > 0 && costDefect,
    then:() => yup
      .string()
      .required("This is a Required field")
      .typeError("This is a Required field"),
    otherwise:()=> yup.string().nullable(),
  }),
  costComment: yup.string().when(["extraCost", "costDefect"], {
    is: (extraCost, costDefect) =>
      extraCost != null && Number(extraCost) > 0 && costDefect,
    then:() => yup
      .string()
      .required("This is a Required field")
      .typeError("This is a Required field"),
    otherwise:()=> yup.string().nullable(),
  }),
  unit: yup.string().when(["costDefect"], {
    is: true,
    then:() => yup
      .string()
      .required("This is a Required field")
      .typeError("This should be a valid Quantity"),
    otherwise:() => yup.string().nullable(),
  }),
  customElement: yup.mixed().when(["isElementOther"], {
    is: true,
    then:() => yup.object().shape({
      value: yup.string().required("This is required"),
      title: yup.string().required("This is required"), // Must be string, matching old form
    }),
    otherwise:() => yup.mixed().notRequired(),
  }),
});

// ===== FORMAT COMPONENTS =====

/**
 * NumericFormatCustom - Currency input formatter
 * Formats numbers with German locale (. for thousands, , for decimals)
 * Only allows positive numbers
 */
export const NumericFormatCustom = (props) => {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        if (floatValue == null) {
          return formattedValue === "";
        } else {
          return floatValue > 0;
        }
      }}
    />
  );
};

NumericFormatCustom.propTypes = {
  inputRef: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * NumberFormatForPercentage - Percentage input formatter
 * Formats percentages with German locale
 * Only allows values between 0-100
 */
export const NumberFormatForPercentage = (props) => {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat 
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        if (floatValue == null) {
          return formattedValue === "";
        } else {
          return floatValue <= 100 && floatValue > 0;
        }
      }}
    />
  );
};

NumberFormatForPercentage.propTypes = {
  inputRef: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
